'use strict'

const { imageFieldKeys, imageFilename, imageMarkup } = require('./image-column')
const { flattenCardFields } = require('./card-layout')

// A table or cards column can show a field from a spreadsheet linked to the
// view's spreadsheet. Forward: this sheet has a relationship column.
// Reverse: another sheet has a relationship column that points here.
// The stored uniqueTitle is the row key. It is not a column on this sheet.

const SYSTEM_FIELDS = [
  { uniqueTitle: 'id', title: 'ID', type: 'number' },
  { uniqueTitle: 'createdAt', title: 'Created At', type: 'dateTime' },
  { uniqueTitle: 'updatedAt', title: 'Updated At', type: 'dateTime' }
]

// Password, file, and nested relationships stay off the picker. Image
// columns are listed so a card, table, map, or calendar can show them.
const HIDDEN_RELATED_TYPES = new Set(['password', 'file', 'relationship'])
const FIELD_NAME = /^[A-Za-z_][A-Za-z0-9_]*$/

function relatedSpecFromKey (key) {

  if (typeof key !== 'string') return null

  const parts = key.split('.')

  if (!parts.length || parts.some(part => !FIELD_NAME.test(part))) return null

  if (parts[0] === 'rel' && parts.length === 3) {

    return {
      relatedSource: 'forward',
      relationshipColumn: parts[1],
      relatedField: parts[2],
      uniqueTitle: key
    }
  }

  if (parts[0] === 'rev' && parts.length === 4) {

    return {
      relatedSource: 'reverse',
      sourceSpreadsheet: parts[1],
      sourceRelationshipColumn: parts[2],
      relatedField: parts[3],
      relatedSpreadSheet: parts[1],
      uniqueTitle: key
    }
  }

  return null
}

function relatedViewColumnKey (spec) {

  if (!spec || !spec.relatedField) return ''

  if (spec.relatedSource === 'reverse') {

    return `rev.${spec.sourceSpreadsheet}.${spec.sourceRelationshipColumn}.${spec.relatedField}`
  }

  return `rel.${spec.relationshipColumn}.${spec.relatedField}`
}

function fieldFormatting (column) {

  const formatting = column && column.valueFormatting

  if (!formatting || !formatting.type) return null

  return formatting
}

function visibleRelatedFields (columns) {

  const fields = SYSTEM_FIELDS.map(field => ({ ...field, formula: '', valueFormatting: null }))
  const seen = new Set(fields.map(field => field.uniqueTitle))

  for (const column of columns || []) {

    if (!column || !column.uniqueTitle || seen.has(column.uniqueTitle)) continue
    if (HIDDEN_RELATED_TYPES.has(column.type)) continue

    seen.add(column.uniqueTitle)
    fields.push({
      uniqueTitle: column.uniqueTitle,
      title: column.title || column.uniqueTitle,
      type: column.type || 'text',
      formula: column.formula || '',
      valueFormatting: fieldFormatting(column)
    })
  }

  return fields
}

function relatedFieldSheet (field) {

  if (!field || !field.relatedField) return ''

  if (field.relatedSource === 'reverse') return field.sourceSpreadsheet || field.relatedSpreadSheet || ''

  return field.relatedSpreadSheet || ''
}

function relatedViewColumnOptions (spreadsheet, allSpreadsheets) {

  if (!spreadsheet) return []

  const sheets = allSpreadsheets || []
  const options = []
  const seen = new Set()

  function push (spec, text) {

    const value = relatedViewColumnKey(spec)

    if (!value || seen.has(value)) return

    seen.add(value)
    options.push({
      value,
      text,
      relatedSource: spec.relatedSource,
      relatedField: spec.relatedField,
      relatedFieldType: spec.relatedFieldType || 'text',
      formula: spec.formula || '',
      valueFormatting: spec.valueFormatting && spec.valueFormatting.type ? spec.valueFormatting : null,
      relatedSpreadSheet: spec.relatedSpreadSheet || '',
      relationshipColumn: spec.relationshipColumn || '',
      sourceSpreadsheet: spec.sourceSpreadsheet || '',
      sourceRelationshipColumn: spec.sourceRelationshipColumn || ''
    })
  }

  for (const column of spreadsheet.columns || []) {

    if (!column || column.type !== 'relationship' || !column.relatedSpreadSheet) continue

    const related = sheets.find(sheet => sheet && sheet.uniqueTitle === column.relatedSpreadSheet)

    if (!related) continue

    const via = column.title || column.uniqueTitle

    for (const field of visibleRelatedFields(related.columns)) {

      push({
        relatedSource: 'forward',
        relationshipColumn: column.uniqueTitle,
        relatedSpreadSheet: related.uniqueTitle,
        relatedField: field.uniqueTitle,
        relatedFieldType: field.type,
        formula: field.formula || '',
        valueFormatting: field.valueFormatting || null
      }, `${via} → ${field.title}`)
    }
  }

  for (const sheet of sheets) {

    if (!sheet || sheet.uniqueTitle === spreadsheet.uniqueTitle || !sheet.columns) continue

    for (const column of sheet.columns) {

      if (!column || column.type !== 'relationship' || column.relatedSpreadSheet !== spreadsheet.uniqueTitle) continue

      const owner = sheet.title || sheet.uniqueTitle
      const via = column.title || column.uniqueTitle

      for (const field of visibleRelatedFields(sheet.columns)) {

        push({
          relatedSource: 'reverse',
          sourceSpreadsheet: sheet.uniqueTitle,
          sourceRelationshipColumn: column.uniqueTitle,
          relatedSpreadSheet: sheet.uniqueTitle,
          relatedField: field.uniqueTitle,
          relatedFieldType: field.type,
          formula: field.formula || '',
          valueFormatting: field.valueFormatting || null
        }, `${owner} (${via}) → ${field.title}`)
      }
    }
  }

  return options
}

function findViewColumnOption (options, value) {

  return (options || []).find(option => option && option.value === value) || null
}

function viewColumnDataType (spreadsheet, selectedValue, options) {

  const option = findViewColumnOption(options, selectedValue)

  if (option && option.relatedSource) {

    if (option.relatedFieldType === 'richText') return 'dataRichText'
    if (option.relatedFieldType === 'image' || option.relatedFieldType === 'signature') return 'dataImage'
    if (option.relatedFieldType === 'file') return 'dataFile'

    return 'dataColumn'
  }

  const column = spreadsheet && spreadsheet.columns
    ? spreadsheet.columns.find(item => item.uniqueTitle === selectedValue)
    : null

  if (!column) return 'dataColumn'
  if (column.type === 'image' || column.type === 'signature') return 'dataImage'
  if (column.type === 'file') return 'dataFile'
  if (column.type === 'richText') return 'dataRichText'

  return 'dataColumn'
}

const RELATED_KEYS = [
  'relatedSource',
  'relatedField',
  'relatedSpreadSheet',
  'sourceSpreadsheet',
  'sourceRelationshipColumn'
]

function assignRelatedViewColumn (component, option) {

  if (!component) return

  if (!option || !option.relatedSource) {

    for (const key of RELATED_KEYS) delete component[key]

    if (component.type !== 'link') delete component.relationshipColumn

    return
  }

  component.relatedSource = option.relatedSource
  component.relatedField = option.relatedField
  component.relatedSpreadSheet = option.relatedSpreadSheet || ''
  component.editable = false

  if (option.relatedSource === 'forward') {

    component.relationshipColumn = option.relationshipColumn
    delete component.sourceSpreadsheet
    delete component.sourceRelationshipColumn

    return
  }

  component.sourceSpreadsheet = option.sourceSpreadsheet
  component.sourceRelationshipColumn = option.sourceRelationshipColumn
  delete component.relationshipColumn
}

function relatedColumnCaption (component) {

  if (!component || !component.relatedField) return ''

  if (component.relatedSource === 'reverse') {

    return `${component.sourceSpreadsheet} (${component.sourceRelationshipColumn}) → ${component.relatedField}`
  }

  if (component.relationshipColumn) {

    return `${component.relationshipColumn} → ${component.relatedField}`
  }

  return component.relatedField
}

function relatedViewColumnSpecs (components) {

  return flattenCardFields(components).filter(component => {

    return component
      && component.uniqueTitle
      && component.relatedField
      && (component.relatedSource === 'forward' || component.relatedSource === 'reverse')
  })
}

function relatedRecordAttributes (columns, requestedFields) {

  const allowed = new Set(SYSTEM_FIELDS.map(field => field.uniqueTitle))
  const imageFields = new Set()

  for (const column of columns || []) {

    if (!column || !column.uniqueTitle || HIDDEN_RELATED_TYPES.has(column.type)) continue

    allowed.add(column.uniqueTitle)
    if (column.type === 'image') imageFields.add(column.uniqueTitle)
  }

  const attributes = ['id']
  const seen = new Set(attributes)

  for (const field of requestedFields || []) {

    if (typeof field !== 'string' || !allowed.has(field) || seen.has(field)) continue
    if (!FIELD_NAME.test(field)) continue

    seen.add(field)
    attributes.push(field)

    if (!imageFields.has(field)) continue

    for (const extra of imageFieldKeys(field)) {

      if (seen.has(extra)) continue

      seen.add(extra)
      attributes.push(extra)
    }
  }

  return attributes
}

function joinRelatedField (records, field) {

  if (!field) return ''

  const values = []

  for (const record of records || []) {

    if (!record) continue

    const value = record[field]

    if (value == null || value === '') continue
    if (typeof value === 'object') continue

    values.push(String(value))
  }

  return values.join(', ')
}

function joinRelatedImageField (records, field) {

  const tags = []
  const names = []

  for (const record of records || []) {

    const tag = imageMarkup(record, field)

    if (!tag) continue

    tags.push(tag)

    const name = imageFilename(record, field)

    if (name) names.push(name)
  }

  return {
    html: tags.join(''),
    filename: names.join(', ')
  }
}

module.exports = {
  relatedViewColumnKey,
  relatedFieldSheet,
  relatedSpecFromKey,
  relatedViewColumnOptions,
  findViewColumnOption,
  viewColumnDataType,
  assignRelatedViewColumn,
  relatedColumnCaption,
  relatedViewColumnSpecs,
  relatedRecordAttributes,
  joinRelatedField,
  joinRelatedImageField
}
